import { formatAmount } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Input } from "@loot-os/ui/components/input";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, ArrowUpDown, ListFilter, Search } from "lucide-react";
import { useState } from "react";

import { useCurrency } from "../lib/currency";
import { formatDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import {
  DEFAULT_SHOPIFY_PURCHASE_SORT,
  nextShopifyPurchaseSort,
  type ShopifyPurchaseSort,
  type ShopifyPurchaseSortKey,
  viewShopifyPurchases,
} from "../lib/userShopifyView";
import { useShopifyShop } from "../lib/useShopifyShop";
import { CopyButton } from "./DetailFields";
import { ExpandableRow } from "./ExpandableRow";
import { ShopifyOrderLink } from "./ShopifyLinks";
import { ShopifyPurchaseDetail } from "./ShopifyPurchaseDetail";
import { TableMessageRow } from "./TableMessageRow";

const COLUMNS = 6;

/**
 * A Shopify customer's box purchases from `shopify.userPurchases`, one row per unit, newest
 * first until a header sorts by paid, status or bought. The filter on the Order header narrows
 * to one order by its name or id. The order opens in the store's admin and copies with one click,
 * and the row opens in place to the purchase's full detail.
 */
export function UserShopifyTab({ endUserId }: { endUserId: string }) {
  const trpc = useTRPC();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const currency = useCurrency();
  const [expanded, setExpanded] = useState<string | null>(null);
  const { shopHandle } = useShopifyShop();
  const query = useQuery(trpc.shopify.userPurchases.queryOptions({ endUserId }));
  const [sort, setSort] = useState<ShopifyPurchaseSort>(DEFAULT_SHOPIFY_PURCHASE_SORT);
  const [search, setSearch] = useState("");
  const all = query.data ?? [];
  const rows = viewShopifyPurchases(all, sort, search);
  const onSort = (key: ShopifyPurchaseSortKey) => setSort((s) => nextShopifyPurchaseSort(s, key));

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <OrderFilterHeader search={search} onSearch={setSearch} />
          <TableHead>Box</TableHead>
          <SortHeader
            label="Paid"
            sortKey="paid"
            sort={sort}
            onSort={onSort}
            className="text-right"
          />
          <SortHeader label="Status" sortKey="status" sort={sort} onSort={onSort} />
          <SortHeader label="Bought" sortKey="bought" sort={sort} onSort={onSort} />
        </TableRow>
      </TableHeader>
      <TableBody>
        {query.isLoading && <TableMessageRow colSpan={COLUMNS}>Loading…</TableMessageRow>}
        {query.error && <TableMessageRow colSpan={COLUMNS}>{query.error.message}</TableMessageRow>}
        {query.isSuccess && all.length === 0 && (
          <TableMessageRow colSpan={COLUMNS}>No Shopify orders.</TableMessageRow>
        )}
        {all.length > 0 && rows.length === 0 && (
          <TableMessageRow colSpan={COLUMNS}>No order matches “{search.trim()}”.</TableMessageRow>
        )}
        {rows.map((p) => (
          <ExpandableRow
            key={p.id}
            detailId={`shopify-purchase-${p.id}`}
            label={`Show ${p.orderName}`}
            isOpen={expanded === p.id}
            onToggle={() => setExpanded(expanded === p.id ? null : p.id)}
            colSpan={COLUMNS}
            detail={
              <ShopifyPurchaseDetail
                tenantId={tenantId}
                purchaseId={p.id}
                shopHandle={shopHandle}
              />
            }
          >
            <TableCell>
              <span className="flex items-center gap-1">
                <ShopifyOrderLink shopHandle={shopHandle} orderGid={p.orderGid}>
                  {p.orderName}
                </ShopifyOrderLink>
                {p.unitIndex > 0 && (
                  <span className="text-muted-foreground"> · {p.unitIndex + 1}</span>
                )}
                {/* The name Shopify's admin searches orders by. */}
                <CopyButton text={p.orderName} label={`Copy ${p.orderName}`} />
              </span>
            </TableCell>
            <TableCell>{p.boxName}</TableCell>
            <TableCell className="text-right tabular-nums">
              {formatAmount(p.paidPriceCents, currency)}
            </TableCell>
            <TableCell>
              <Badge variant={p.status === "voided" ? "outline" : "secondary"}>{p.status}</Badge>
            </TableCell>
            <TableCell className="text-muted-foreground">{formatDate(p.createdAt)}</TableCell>
          </ExpandableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** The Order column header, with a filter button that pops out a search by order name or id. */
function OrderFilterHeader({
  search,
  onSearch,
}: {
  search: string;
  onSearch: (search: string) => void;
}) {
  const active = search.trim() !== "";
  return (
    <TableHead>
      <span className="inline-flex items-center gap-1">
        Order
        <Popover>
          <PopoverTrigger
            render={
              <button
                type="button"
                aria-label="Filter by order"
                title="Filter by order"
                className="inline-flex items-center hover:text-foreground"
              />
            }
          >
            <ListFilter className={active ? "size-3.5 text-primary" : "size-3.5 opacity-40"} />
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                autoFocus
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder="#1042 or order id"
                aria-label="Filter by order"
                className="pl-8"
              />
            </div>
          </PopoverContent>
        </Popover>
      </span>
    </TableHead>
  );
}

/** A sortable column header; the active column shows its direction. */
function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string;
  sortKey: ShopifyPurchaseSortKey;
  sort: ShopifyPurchaseSort;
  onSort: (key: ShopifyPurchaseSortKey) => void;
  className?: string;
}) {
  const active = sort.key === sortKey ? sort.direction : null;
  const Icon = active === "asc" ? ArrowUp : active === "desc" ? ArrowDown : ArrowUpDown;
  return (
    <TableHead
      className={className}
      aria-sort={active === "asc" ? "ascending" : active === "desc" ? "descending" : "none"}
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-foreground"
        onClick={() => onSort(sortKey)}
      >
        {label}
        <Icon className={active ? "size-3.5" : "size-3.5 opacity-40"} />
      </button>
    </TableHead>
  );
}
